import type {
  ChangeEvent,
  ComponentRef,
  Dispatch,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  SetStateAction,
  SyntheticEvent,
  TouchEvent,
} from "react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useMediaQuery } from "react-responsive";

import { usePlatformDetector } from "@ui/hooks/usePlatformDetector";
import { formatTimeline } from "@ui/utils/formatTime/formatTime";
import {
  exitFullscreen,
  fullscreenEnabled,
  isFullscreen,
  requestFullscreen,
  requestFullscreenMobileIOS,
} from "@ui/utils/fullscreenHelpers/fullscreenHelpers";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { FullscreenCloseIcon } from "@jti/brand-icons/fullscreen-close";
import { FullscreenOpenIcon } from "@jti/brand-icons/fullscreen-open";
import { PlayIcon } from "@jti/brand-icons/play";
import { ReplayIcon } from "@jti/brand-icons/replay";
import { SoundOffIcon } from "@jti/brand-icons/sound-off";
import { SoundOnIcon } from "@jti/brand-icons/sound-on";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { VideoControls } from "./components/VideoControls";
import { VideoPlayerInfo } from "./components/VideoPlayerInfo";
import { VideoPlayerPoster } from "./components/VideoPlayerPoster";
import { getFileName } from "./helpers/helpers";

import styles from "./VideoPlayer.module.scss";

export interface VideoPlayerProps {
  desktopSrc: string;
  tabletSrc?: string;
  mobileSrc?: string;
  hostedExternally?: boolean;
  externalLinkVideo?: string;
  loop?: boolean;
  poster?: string;
  alt?: string;
  title?: string;
  description?: string;
  playLabel?: string;
  replayLabel?: string;
  soundOnLabel?: string;
  muteLabel?: string;
  showFullscreenLabel?: string;
  showDuration?: boolean;
  fallbackDuration?: number;
  autoPlay?: boolean;
  preventDownload?: boolean;
  showControls?: boolean;
  showControlsWhenPlaying?: boolean;
  preventMainPlayButtonClick?: boolean;
  showFullscreen?: boolean;
  className?: string;
  contentSize?: boolean;
  preload?: boolean;
  removeRoundedCorners?: boolean;
  keepOriginalAspectRatio?: boolean;
  squareContainer?: boolean;
  shouldStopVideo?: boolean;
  themeClass?: "dark-theme" | "light-theme";
  videoClassName?: string;
  setIsVideoLoaded?: Dispatch<SetStateAction<boolean>>;
  isFirstElement?: boolean;
  isMainPlayButtonAllowed?: boolean;
  children?: ReactNode;
  onEnded?: () => void;
  disableSeek?: boolean;
  seekLabel?: string;
  volumeLabel?: string;
  watchTimeTrigger?: {
    percentageToWatch?: number;
    onWatchTimeReached: () => void;
  };
  onPlayingChange?: Dispatch<SetStateAction<boolean>>;
  enforceCustomControls?: boolean;
}

type SeekMouseUpHandler = (e: MouseEvent<HTMLInputElement> | TouchEvent<HTMLInputElement>) => void;

export const VideoPlayer: FC<VideoPlayerProps> = memo(
  ({
    desktopSrc = "",
    tabletSrc = "",
    mobileSrc = "",
    hostedExternally = false,
    externalLinkVideo,
    loop = false,
    autoPlay = false,
    poster = "",
    alt = "",
    title = "",
    description,
    playLabel,
    replayLabel,
    soundOnLabel,
    muteLabel,
    showFullscreenLabel,
    preventDownload,
    showDuration = false,
    fallbackDuration,
    showControls = false,
    showControlsWhenPlaying = false,
    preventMainPlayButtonClick = false,
    showFullscreen = false,
    className,
    contentSize = false,
    preload = false,
    removeRoundedCorners,
    keepOriginalAspectRatio = false,
    squareContainer = false,
    shouldStopVideo = false,
    themeClass = "dark-theme",
    videoClassName,
    setIsVideoLoaded,
    isFirstElement,
    watchTimeTrigger,
    isMainPlayButtonAllowed = true,
    onEnded,
    disableSeek = false,
    seekLabel,
    volumeLabel,
    children,
    onPlayingChange,
    enforceCustomControls = false,
  }) => {
    const config = useConfig();
    const isPreventDownload = preventDownload ?? (config.blockVideoDownload || false);

    const isDesktop = useMediaQuery({ query: "(min-width: 960px)" });
    const isTablet = useMediaQuery({ query: "(min-width: 480px) and (max-width: 959px)" });
    const isMobile = useMediaQuery({ maxWidth: 479 });

    const playerRef = useRef<ComponentRef<typeof ReactPlayer> | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(autoPlay && !hostedExternally);
    const [played, setPlayed] = useState(0);
    const [volume, setVolume] = useState(1);
    const [duration, setDuration] = useState(0);
    const validFallbackDuration =
      typeof fallbackDuration === "number" && Number.isFinite(fallbackDuration) && fallbackDuration > 0
        ? fallbackDuration
        : 0;
    const resolvedDuration = Number.isFinite(duration) && duration > 0 ? duration : validFallbackDuration;
    const [muted, setMuted] = useState(autoPlay);
    const [isPosterActive, setIsPosterActive] = useState(false);
    const videoWrapperRef = useRef<HTMLDivElement>(null);
    const [videoEnded, setVideoEnded] = useState(false);
    const [videoSrc, setVideoSrc] = useState<string>("");
    const previousVideoSrcRef = useRef("");
    const [fullScreenOpenIcon, setFullScreenOpenIcon] = useState<boolean>(true);
    const stopGTMEvent = loop && autoPlay && !showControls;
    const hasAutoPlayedRef = useRef(false);
    const stopTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isShowControls =
      (isPlaying && showControlsWhenPlaying) || (showControls ?? (config.showVideoControls || false));
    const hasTriggeredRef = useRef(false);
    const hasCompletedRef = useRef(false);
    const firedProgressThresholdsRef = useRef<Set<number>>(new Set());
    const PROGRESS_THRESHOLDS = [10, 25, 50, 75];
    const PERCENT_BASE = 100;
    const WATCH_TIME_PRECISION = 100;

    const watchTimeTriggerHandler = (syncPlayed: number, syncedDuration = duration) => {
      if (!watchTimeTrigger || syncedDuration === 0) return;
      const { percentageToWatch, onWatchTimeReached } = watchTimeTrigger;
      const DEFAULT_WATCH_TIME_FRACTION = 1;
      const watchTimeFraction =
        percentageToWatch !== undefined && percentageToWatch > 0 && percentageToWatch <= 100
          ? percentageToWatch / 100
          : DEFAULT_WATCH_TIME_FRACTION;
      const roundedPlayed = Math.floor(syncPlayed * PERCENT_BASE) / PERCENT_BASE;
      if (!hasTriggeredRef.current && roundedPlayed >= watchTimeFraction) {
        hasTriggeredRef.current = true;
        onWatchTimeReached();
      }
    };

    useEffect(() => {
      const isPosterNonEmpty = poster.length > 0;
      setIsPosterActive(isPosterNonEmpty && !autoPlay);
    }, [poster, autoPlay]);

    useEffect(() => {
      let nextVideoSrc: string | undefined;

      if (hostedExternally && externalLinkVideo && externalLinkVideo.length > 0) {
        nextVideoSrc = externalLinkVideo;
      } else if (isMobile) {
        nextVideoSrc = mobileSrc || tabletSrc || desktopSrc;
      } else if (isTablet) {
        nextVideoSrc = tabletSrc || desktopSrc;
      } else if (isDesktop) {
        nextVideoSrc = desktopSrc;
      }

      if (nextVideoSrc === undefined || nextVideoSrc === previousVideoSrcRef.current) return;

      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }

      setDuration(0);
      setPlayed(0);
      setVideoEnded(false);
      hasCompletedRef.current = false;
      hasTriggeredRef.current = false;
      firedProgressThresholdsRef.current = new Set();
      previousVideoSrcRef.current = nextVideoSrc;
      setVideoSrc(nextVideoSrc);
    }, [hostedExternally, externalLinkVideo, mobileSrc, tabletSrc, desktopSrc, isMobile, isTablet, isDesktop]);

    useEffect(() => {
      if (isPosterActive && isPlaying) {
        setIsPosterActive(false);
      }
    }, [isPlaying, isPosterActive]);

    useEffect(() => {
      if (shouldStopVideo) {
        setIsPlaying(false);
      }
    }, [shouldStopVideo]);

    useEffect(() => {
      onPlayingChange?.(isPlaying);
    }, [isPlaying, onPlayingChange]);

    const platform = usePlatformDetector();

    const iosPlatform = platform === "ios";
    const toggleFullscreen = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const video = videoWrapperRef.current;
      if (!video) return;
      const videoTag = video.querySelector("video");
      const isIOSMobileView = isMobile && iosPlatform;
      const isFullScreenDisabled = isIOSMobileView ? false : !fullscreenEnabled();
      if (isFullScreenDisabled) return;

      if (isFullscreen()) {
        setFullScreenOpenIcon(true);
        exitFullscreen();
      } else {
        setFullScreenOpenIcon(false);
        isIOSMobileView && videoTag ? requestFullscreenMobileIOS(videoTag) : requestFullscreen(video);
      }
    };

    const dispatchPlayGTMEvent = () => {
      dispatchTrackingEvent(TrackingEvents.VideoClick, { video_title: title || getFileName(desktopSrc) });
    };

    const dispatchStopGTMEvent = () => {
      let watchTime = Math.round(resolvedDuration * played * WATCH_TIME_PRECISION) / WATCH_TIME_PRECISION;
      let completion = Math.round(played * PERCENT_BASE);
      const isShortVideoAboutToEnd = resolvedDuration <= 15 && completion >= 85;

      if (isShortVideoAboutToEnd) {
        completion = PERCENT_BASE;
        watchTime = Math.round(resolvedDuration * WATCH_TIME_PRECISION) / WATCH_TIME_PRECISION;
      }

      dispatchTrackingEvent(TrackingEvents.VideoStop, {
        video_title: title || getFileName(desktopSrc),
        video_watchtime: watchTime,
        video_completion: completion,
      });
    };

    const dispatchCompleteGTMEvent = () => {
      if (hasCompletedRef.current) return;
      hasCompletedRef.current = true;

      const watchTime =
        resolvedDuration > 0 ? Math.round(resolvedDuration * WATCH_TIME_PRECISION) / WATCH_TIME_PRECISION : 0;

      dispatchTrackingEvent(TrackingEvents.VideoComplete, {
        video_title: title || getFileName(desktopSrc),
        video_watchtime: watchTime,
        video_percent: PERCENT_BASE,
      });
    };

    const dispatchProgressGTMEvents = (currentPlayed: number, currentDuration: number) => {
      if (currentDuration <= 0) return;

      const percent = currentPlayed * PERCENT_BASE;

      PROGRESS_THRESHOLDS.forEach((threshold) => {
        if (percent >= threshold && !firedProgressThresholdsRef.current.has(threshold)) {
          firedProgressThresholdsRef.current.add(threshold);
          const watchTime =
            Math.round(currentDuration * (threshold / PERCENT_BASE) * WATCH_TIME_PRECISION) / WATCH_TIME_PRECISION;

          dispatchTrackingEvent(TrackingEvents.VideoProgress, {
            video_title: title || getFileName(desktopSrc),
            video_watchtime: watchTime,
            video_percent: threshold,
          });
        }
      });
    };

    const handlePlayPause = useCallback(() => {
      if (hostedExternally && !enforceCustomControls) return false;

      setIsPlaying((prevIsPlaying) => {
        if (videoEnded && !loop) {
          setVideoEnded(false);
          return true;
        }
        return !prevIsPlaying;
      });

      if (isPosterActive) {
        setIsPosterActive(false);
      }
    }, [videoEnded, loop, isPosterActive, hostedExternally, enforceCustomControls]);

    useEffect(() => {
      const shouldAutoPlay = autoPlay && !hostedExternally;
      if (shouldAutoPlay) {
        setIsPlaying(true);
      }
    }, [autoPlay, hostedExternally]);

    const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
      const mediaDuration = event.currentTarget.duration;
      const syncedDuration = Number.isFinite(mediaDuration) && mediaDuration > 0 ? mediaDuration : resolvedDuration;
      const syncedPlayed = syncedDuration > 0 ? event.currentTarget.currentTime / syncedDuration : 0;

      watchTimeTriggerHandler(syncedPlayed, syncedDuration);
      if (!stopGTMEvent) {
        dispatchProgressGTMEvents(syncedPlayed, syncedDuration);
      }
      setPlayed(syncedPlayed);

      if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
        setDuration(mediaDuration);
      }
    };

    const handleSeekChange = (e: ChangeEvent<HTMLInputElement>) => {
      setPlayed(parseFloat(e.target.value));
    };

    const handleSeekMouseUp: SeekMouseUpHandler = (e) => {
      const isTouchEvent = "changedTouches" in e;
      const inputElement = isTouchEvent
        ? ((e as TouchEvent<HTMLInputElement>).changedTouches[0].target as HTMLInputElement)
        : (e.currentTarget as HTMLInputElement);

      const value = parseFloat(inputElement.value);

      if (playerRef.current) {
        playerRef.current.currentTime = value * resolvedDuration;
      }

      if (!stopGTMEvent && value >= 1 && resolvedDuration > 0) {
        dispatchCompleteGTMEvent();
      }
    };

    const handleDurationChange = (event: SyntheticEvent<HTMLVideoElement>) => {
      const mediaDuration = event.currentTarget.duration;
      setDuration(Number.isFinite(mediaDuration) && mediaDuration > 0 ? mediaDuration : 0);
    };

    const handleVolumeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value);
      setVolume(newVolume);
      if (newVolume <= 0) {
        setMuted(true);
        setVolume(newVolume);
      } else {
        setMuted(false);
      }
    }, []);

    const handleMute = (event: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMuted((muted) => !muted);
    };

    const preventContextMenu = (event: MouseEvent<HTMLDivElement>) => {
      if (isPreventDownload) {
        event.preventDefault();
      }
    };

    const handleEnd = () => {
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }

      if (!stopGTMEvent) {
        dispatchCompleteGTMEvent();
      }

      setVideoEnded(true);
      setIsPlaying(false);
      onEnded?.();
    };

    const handleStart = () => {
      hasCompletedRef.current = false;
      firedProgressThresholdsRef.current = new Set();
      setVideoEnded(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);

      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }

      if (autoPlay && !hasAutoPlayedRef.current) {
        hasAutoPlayedRef.current = true;
        return;
      }

      if (!stopGTMEvent) {
        dispatchPlayGTMEvent();
      }
    };

    const playerPreload = hostedExternally ? undefined : autoPlay || preload ? "metadata" : "none";
    const useNativePlayerControls = hostedExternally && !enforceCustomControls;
    const isMainPlayerInteractive = !preventMainPlayButtonClick && isMainPlayButtonAllowed;
    const mainPlayerLabel = videoEnded ? replayLabel : playLabel;
    const mainPlayerAriaLabel = isMainPlayerInteractive ? mainPlayerLabel : undefined;

    const playerConfig = hostedExternally
      ? {
          youtube: {
            rel: 0 as const,
            autoplay: 0 as const,
          },
        }
      : undefined;

    const toggleMainPlaySurface = useCallback(
      (target: EventTarget | null) => {
        if (!isMainPlayerInteractive) return;

        setIsPosterActive(false);
        const isClickWithinControls = target instanceof Element && target.closest('div[class*="videoPlayerControls"]');

        if (!isClickWithinControls) {
          handlePlayPause();
        }
      },
      [handlePlayPause, isMainPlayerInteractive]
    );

    const handleMainPlayButtonClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        toggleMainPlaySurface(event.target);
      },
      [toggleMainPlaySurface]
    );

    const handleMainPlayButtonKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isMainPlayerInteractive || event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        toggleMainPlaySurface(event.currentTarget);
      },
      [isMainPlayerInteractive, toggleMainPlaySurface]
    );

    const renderMainPlayIcon = () => {
      if (!isMainPlayButtonAllowed) {
        return;
      }

      return (
        <button
          aria-label={playLabel}
          className={classNames(styles.videoPlayerPlayIcon)}
          data-testid="videPlayerPosterIcnBtn"
        >
          <PlayIcon className={styles.iconPlay} height="2rem" width="2rem" />
        </button>
      );
    };

    const renderReplayIcon = () => {
      if (!isMainPlayButtonAllowed) {
        return;
      }

      return (
        <button
          aria-label={replayLabel}
          className={classNames(styles.videoPlayerPlayIcon)}
          data-testid="videoPlayerReplayButton"
        >
          <ReplayIcon className={styles.iconReplay} />
        </button>
      );
    };

    const videoPlayerWrapperClassName = classNames("videoPlayerWrapper", className, themeClass, {
      [styles.videoPlayerWrapperNosrc]: desktopSrc.length === 0,
      [styles.videoPlayerWrapperExternal]: hostedExternally,
      [styles.videoPlayerWrapperFullscreen]: !fullScreenOpenIcon,
      [styles.videoPlayerWrapperContentSize]: contentSize,
      [styles.videoPlayerWrapperSquare]: squareContainer,
      [styles.noRoundedCorners]: removeRoundedCorners,
    });

    const handleVideoPause = () => {
      setIsPlaying(false);

      if (!stopGTMEvent) {
        stopTimerRef.current = setTimeout(() => {
          dispatchStopGTMEvent();
          stopTimerRef.current = null;
        }, 200);
      }
    };

    const handleLoad = useCallback(() => {
      if (isFirstElement && setIsVideoLoaded) {
        setIsVideoLoaded(true);
      }
    }, [isFirstElement, setIsVideoLoaded]);

    const FullScreenIcon =
      isMobile && iosPlatform ? FullscreenOpenIcon : fullScreenOpenIcon ? FullscreenOpenIcon : FullscreenCloseIcon;

    return (
      <div className={classNames("videoContainer", videoClassName, styles.videoPlayerContainer)}>
        <div
          ref={videoWrapperRef}
          onClick={handleMainPlayButtonClick}
          onKeyDown={handleMainPlayButtonKeyDown}
          className={videoPlayerWrapperClassName}
          data-testid="video-element"
          dir="ltr"
          role={isMainPlayerInteractive ? "button" : undefined}
          tabIndex={isMainPlayerInteractive ? 0 : undefined}
          aria-label={mainPlayerAriaLabel}
        >
          {!isShowControls && !hostedExternally && !isPlaying && !videoEnded && renderMainPlayIcon()}
          {!isShowControls && hostedExternally && isPosterActive && renderMainPlayIcon()}
          {!isShowControls && !hostedExternally && videoEnded && renderReplayIcon()}
          {isPosterActive && <VideoPlayerPoster poster={poster} alt={alt} title={title} />}
          {isShowControls && (!hostedExternally || enforceCustomControls) && (
            <div className={styles.videoPlayerViewportIconsWrapper}>
              <div className={styles.videoPlayerSound}>
                <button
                  aria-label={!muted ? soundOnLabel : muteLabel}
                  className={classNames(styles.videoPlayerButton)}
                  onClick={handleMute}
                  data-testid="videoControlsMuteButton"
                >
                  {!muted ? (
                    <SoundOnIcon className={styles.iconSpeaker} />
                  ) : (
                    <SoundOffIcon className={styles.iconSpeaker} />
                  )}
                </button>
              </div>
            </div>
          )}
          {(!hostedExternally || enforceCustomControls) && showFullscreen && (
            <button
              aria-label={showFullscreenLabel}
              className={styles.videoPlayerFullscreenButton}
              data-testid="videoPlayerFullscreenButton"
              onClick={toggleFullscreen}
              type="button"
            >
              <FullScreenIcon className={classNames(styles.iconFullscreen)} />
            </button>
          )}
          <div
            className={classNames("videoPlayerAspectRatioBox", {
              [styles.videoPlayerKeepOriginalAspectRatio]: keepOriginalAspectRatio,
              [styles.noRoundedCorners]: removeRoundedCorners,
            })}
          >
            <ReactPlayer
              suppressHydrationWarning
              src={videoSrc}
              ref={playerRef}
              playing={isPlaying}
              volume={volume}
              controls={useNativePlayerControls}
              preload={playerPreload}
              width="100%"
              height="100%"
              onTimeUpdate={handleTimeUpdate}
              onDurationChange={handleDurationChange}
              onEnded={handleEnd}
              onStart={handleStart}
              onPlay={handlePlay}
              loop={loop}
              muted={muted}
              config={playerConfig}
              onPause={handleVideoPause}
              playsInline
              onReady={handleLoad}
            />
            {children}
          </div>
          {isPreventDownload && !hostedExternally && (
            <div onContextMenu={preventContextMenu} className={styles.videoPlayerDownloadLayer} />
          )}
          {isShowControls && (!hostedExternally || enforceCustomControls) && (
            <VideoControls
              onPlayPause={handlePlayPause}
              playing={isPlaying}
              played={played}
              onSeekChange={handleSeekChange}
              onSeekMouseUp={handleSeekMouseUp}
              onVolumeChange={handleVolumeChange}
              elapsedTime={formatTimeline(played * resolvedDuration)}
              totalDuration={formatTimeline(resolvedDuration)}
              onMute={handleMute}
              volume={volume}
              muted={muted}
              loop={loop}
              videoEnded={videoEnded}
              disableSeek={disableSeek}
              playLabel={playLabel}
              muteLabel={muteLabel}
              soundOnLabel={soundOnLabel}
              seekLabel={seekLabel}
              volumeLabel={volumeLabel}
            />
          )}
        </div>
        <VideoPlayerInfo duration={resolvedDuration} showDuration={showDuration} description={description} />
      </div>
    );
  }
);
